import { Controller } from "@hotwired/stimulus";
import api from "@/utils/api";
import {getCheckBoxIds, getCheckBoxLabel} from "@/utils/helpers";
import noonImage from "@assets/images/noon.jpg"
const LikeDefaultCss = "cursor-pointer focus:outline-none active:text-gray-400 transition duration-150 ease-in-out text-gray-200";
const LikeActiveCss = "cursor-pointer active:text-gray-200 transition duration-150 ease-in-out text-yellow-noon";

export default class extends Controller {
  static targets = [
    "filterForm", "remoteSubmit",
    "fontEditor", "fontEditorAll", "fontsContainer",
    "fontSizeLabel", "fontSizeAll",
    "fontWeightsCountLabel", "fontWeightsCountAll",
    "searchField", "categoryUseCheckBoxes", "categoryStyleCheckBoxes", "fileTypeCheckBoxes", "orderByOption", "orderByLabel", "orderByItem",
    "categoryUseLabel", "categoryStyleLabel", "fileTypeLabel", "totalCount",
    "variantTraitCheckBoxes", "variantTraitLabel",
    "notFoundTemplate",
    "outlineToggleButton", "outlineCheckbox", "fontVariantsCount"
  ];

  static values = {
    page: Number,
    filterApi: String,
    loading: Boolean,
    finished: Boolean,
    params: Object,
    userSignedIn: Boolean,
    displayAdsense: Boolean,
    defaultFontSize: Number,
    fontPartial: String,
    cdnHost: String,
    defaultCategoryUseLabel: String,
    defaultCategoryStyleLabel: String,
    defaultVariantTraitLabel: String,
    defaultFileTypeLabel: String,
    dropFilterFormat: String,
    dropFilterHint: String,
    alternatives: { type: Array, default: [] },
    textOutline: { type: Boolean, default: false }
  };

  initialize() {

  }

  updateParams(key, value) {
    let params = this.paramsValue;
    if (value === "" || value.length === 0) {
      delete params[key];
    } else {
      params[key] = value;
    }
    this.paramsValue = params;
  }

  onResizeSliderScroll(e) {
  }

  onResizeSliderChange(e) {
    this.resizeFonts();
  }

  onWeightsCountSliderScroll(e) {
  }

  onWeightsCountSliderChange(e) {
    const fontWeightsCount = this.hasFontWeightsCountAllTarget ? this.fontWeightsCountAllTarget.value : null;
    if (fontWeightsCount === null) {
      return;
    }

    this.updateParams("weights_count", fontWeightsCount);
    this.reloadFonts();
  }

  onTyping(e) {
    this.retypeFonts();
  }

  onFilterChange(e) {
    this.renderCheckBoxLabels();
    this.updateParams("category_use_ids", getCheckBoxIds(this.categoryUseCheckBoxesTargets));
    this.updateParams("category_style_ids", getCheckBoxIds(this.categoryStyleCheckBoxesTargets));
    this.updateParams("variant_traits", getCheckBoxIds(this.variantTraitCheckBoxesTargets));
    this.updateParams("file_types", getCheckBoxIds(this.fileTypeCheckBoxesTargets));
    this.updateParams("order_by", this.orderByOptionTarget.value);
    this.reloadFonts();
  }

  onOrderBySelect(e) {
    const value = e.currentTarget.dataset.value;
    const label = e.currentTarget.dataset.label;
    this.orderByOptionTarget.value = value;
    if (this.hasOrderByLabelTarget) {
      this.orderByLabelTarget.innerText = label;
    }
    this.orderByItemTargets.forEach((item) => {
      if (item.dataset.value === value) {
        item.classList.add("bg-gray-100", "dark:bg-gray-800", "font-medium");
      } else {
        item.classList.remove("bg-gray-100", "dark:bg-gray-800", "font-medium");
      }
    });
    this.onFilterChange(e);
  }

  renderCheckBoxLabels() {
    if (this.hasCategoryUseLabelTarget) {
      this.categoryUseLabelTarget.innerText = getCheckBoxLabel(this.categoryUseCheckBoxesTargets, this.defaultCategoryUseLabelValue)
    }

    if (this.hasCategoryStyleLabelTarget) {
      this.categoryStyleLabelTarget.innerText = getCheckBoxLabel(this.categoryStyleCheckBoxesTargets, this.defaultCategoryStyleLabelValue);
    }

    if (this.hasVariantTraitLabelTarget) {
      this.variantTraitLabelTarget.innerText = getCheckBoxLabel(this.variantTraitCheckBoxesTargets, this.defaultVariantTraitLabelValue);
    }

    if (this.hasFileTypeLabelTarget) {
      this.fileTypeLabelTarget.innerText = getCheckBoxLabel(this.fileTypeCheckBoxesTargets, this.defaultFileTypeLabelValue);
    }
  }

  onTypeSearchInput = (e) => {
    const keyCode = e.keyCode || e.which;
    if (keyCode === 13) {
      e.preventDefault();
      const trimKeyword = e.target.value.trim();
      this.updateParams("search", trimKeyword);
      this.reloadFonts();
      return;
    }
  }

  retypeFonts() {
    const fontEditorValue = this.hasFontEditorAllTarget ? this.fontEditorAllTarget.value : null;
    if (fontEditorValue === null) {
      return;
    }

    this.fontEditorTargets.forEach((target) => {
      target.value = fontEditorValue;
    });
  }

  resizeFonts() {
    const fontSize = this.hasFontSizeAllTarget ? this.fontSizeAllTarget.value : null;
    if (fontSize === null) {
      return;
    }

    this.updateParams("size", fontSize);
    this.updateUrlParams();

    if (this.fontPartialValue === "grid") {
      this.fontEditorTargets.forEach((target) => {
        target.style.fontSize = `${fontSize}px`;
        target.style.height = "";
        target.style.height = fontSize * 3 + "px"
      });
    } else {
      this.fontEditorTargets.forEach((target) => {
        if (target.tagName === "IMG") {
          target.style.height = "";
          target.style.height = `${parseFloat(fontSize) + 7}px`;
        } else {
          target.style.fontSize = `${fontSize}px`;
          target.style.height = `${parseFloat(fontSize) + 7}px`;
        }
      });
    }
  }

  listViewTemplate(font) {
    const {
      id, name, style, thumbnail_cdn_key, cdn_server_html, polymorphic_show_path
    } = font;
    const fontSizeAllValue = this.hasFontSizeAllTarget ? this.fontSizeAllTarget.value : null;

    if (thumbnail_cdn_key) {
      let fontImageHeight = "37px";
      if (fontSizeAllValue !== null) {
        fontImageHeight = `${parseFloat(fontSizeAllValue) + 7}px`
      }
      return `<a class="list-view-font lg:list-view-font" data-turbolinks="false" href="${polymorphic_show_path}">
    <img class="font-image-preview" style="height: ${fontImageHeight};" alt="${name}" data-fonts-target="fontEditor" src="${this.cdnHostValue}/${thumbnail_cdn_key}" />
</a>`
    } else {
      let fontSpanStyle = style;
      if (fontSizeAllValue !== null) {
        fontSpanStyle = `${style};font-size: ${fontSizeAllValue}px;`
      }

      return `<a class="list-view-font lg:list-view-font" data-turbolinks="false" href="${polymorphic_show_path}">
    <style>${cdn_server_html}</style>
    <span style="${fontSpanStyle};" data-fonts-target="fontEditor" id="fontEditor${id}">${name}</span>
</a>`
    }
  }

  gridViewTemplate(font) {
    const {
      id, name, name_ko, name_en, locale_based_style, ph_content,
      loaded_current_user_font,
      creator_description, thumbnail_cdn_key, default_variant, cdn_server_html,
      variant_summary, polymorphic_show_path,
      is_market, card_preview_image_cdn_url
    } = font;

    const { style, font_family_token } = default_variant;
    const fontSizeAllValue = this.hasFontSizeAllTarget ? this.fontSizeAllTarget.value : null;

    let fontCardHeader = `<span class="text-2xl group-hover-noon-background" style="${style}">${name_ko}</span>`;
    if (thumbnail_cdn_key) {
      fontCardHeader = `<div class="h-8"><img class="h-full object-contain group-hover-noon-background font-image-preview" loading="lazy" alt="${name}" src="${this.cdnHostValue}/${thumbnail_cdn_key}" /></div>`
    }

    let textAreaStyle = style;
    if (fontSizeAllValue !== null) {
      textAreaStyle = `${style};font-size: ${fontSizeAllValue}px; height: ${fontSizeAllValue * 3}px;`
    }

    let creatorDescriptionArea;
    if (document.documentElement.lang === "ko") {
      creatorDescriptionArea = `<p class="text-sm font-bold truncate" style="${style}">${creator_description}</p>`;
    } else {
      creatorDescriptionArea = `<p class="text-sm font-bold truncate" style="${style}">${name_en || name}</p>`;
    }

    // "9가지 굵기 · 이탤릭 · 가변" (Font#variant_summary). 적을 것이 없으면 null 이다.
    let familiesCountArea = `<div></div>`;
    if (variant_summary) {
      familiesCountArea = `<p class="text-sm text-gray-900 dark:text-gray-100 truncate" style="${locale_based_style}" data-fonts-target="fontVariantsCount">${variant_summary}</p>`
    }

    // 마켓 폰트일 경우 프리뷰 이미지로 표시
    let fontContentArea;
    if (is_market) {
      fontContentArea = `<a href="${polymorphic_show_path}" class="flex-1 w-full h-full">
    <div class="w-full h-full"
         style="background-size: cover;
           background-position: 50% 50%;
           background-repeat: no-repeat;
           background-image: url('${card_preview_image_cdn_url}')"></div>
  </a>`
    } else {
      const editorValue = this.hasFontEditorAllTarget ? this.fontEditorAllTarget.value : "";
      const fontTextArea = `<textarea style="${textAreaStyle};" placeholder="${ph_content}" id="fontEditor${id}" class="text-gray-900 dark:text-gray-100 bg-white dark:bg-zinc-800 text-3xl leading-snug resize-none h-36 min-h-36 w-full appearance-none placeholder-current p-0 outline-none border-none focus:outline-none focus:ring-0 focus:border-none" spellcheck="false" lang="ko" name="editor" data-fonts-target="fontEditor">${editorValue}</textarea>`
      fontContentArea = `<div class="font-loading pt-4 px-5 flex-1 overflow-hidden" data-font-family-token="${font_family_token}">
    ${fontTextArea}
  </div>`
    }

    return `<div class="min-w-[300px] h-[300px] text-gray-900 dark:text-gray-100 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-600 rounded-sm group flex flex-col justify-between hover:shadow-md duration-300">
    <style>${cdn_server_html}</style>
  <a class="pt-4 px-5" data-turbolinks="false" href="${polymorphic_show_path}">
    <div class="flex">
      <div class="flex-1">${fontCardHeader}</div>
      <div class="text-white dark:text-zinc-800 group-hover:text-gray-900 dark:group-hover:text-gray-100">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 stroke-current" width="44" height="44" viewBox="0 0 12 24" stroke-width="1" stroke="#2c3e50" fill="none" stroke-linecap="round" stroke-linejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
          <polyline points="9 6 15 12 9 18"></polyline>
        </svg>
      </div>
    </div>
    ${creatorDescriptionArea}
  </a>
  <div class="border-t border-zinc-200 dark:border-zinc-600 mt-4 group-hover:mx-0 group-hover:border-yellow-noon group-hover:border-t"></div>
  ${fontContentArea}
  <div class="px-5 py-4 flex justify-between items-center">
    ${familiesCountArea}
    <div data-controller="fonts--like"
         data-fonts--like-id-value="${id}"
         data-fonts--like-active-class="${LikeActiveCss}"
         data-fonts--like-default-class="${LikeDefaultCss}"
         data-fonts--like-user-signed-in-value="${this.userSignedInValue}"
         data-action="click->fonts--like#toggleFont"
         data-fonts--like-target="toggler"
         class="${loaded_current_user_font ? LikeActiveCss : LikeDefaultCss}">
      <svg xmlns="http://www.w3.org/2000/svg"
           class="h-6 w-6 stroke-current fill-current pointer-events-none"
           width="44" height="44" viewBox="0 0 24 24" stroke-width="1.5" stroke="#2c3e50" fill="none" stroke-linecap="round" stroke-linejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
        <path d="M12 17.75l-6.172 3.245l1.179 -6.873l-5 -4.867l6.9 -1l3.086 -6.253l3.086 6.253l6.9 1l-5 4.867l1.179 6.873z"/>
      </svg>
    </div>
  </div>
</div>`
  }

  squareAdTemplate() {
    let adBox;
    if (process.env.NODE_ENV === 'production') {
      adBox = `<div class="group min-h-auto" data-controller="components--adsense">
        <script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1314414100334102"
             crossorigin="anonymous"></script>
        <!-- square-ad-every-fonts -->
        <ins class="adsbygoogle"
             style="display:block"
             data-ad-client="ca-pub-1314414100334102"
             data-ad-slot="9360101619"
             data-ad-format="auto"
             data-full-width-responsive="true"></ins>
      </div>`;
    } else {
      adBox = `<div data-controller="components--adsense" class="bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-600 rounded-sm group flex items-center justify-center min-h-auto">
    <div class="flex flex-col items-center justify-center" style="height: 280px; width: 100%;">
      <img src="${noonImage}" class="h-16 w-auto"/>
      <p class="text-gray-500 dark:text-gray-300 mt-2 text-sm">눈누 AD</p>
    </div>
  </div>`
    }

    return adBox;
  }

  fireFontLoader(tokens) {
    // Filter out null, undefined, empty strings, and whitespace-only strings (like Ruby's compact_blank)
    const compactedTokens = tokens.filter(token => {
      return token != null && token.toString().trim() !== '';
    });
    
    if (WebFont && compactedTokens.length > 0) {
      // 글꼴 CSS 는 카드가 이미 넣었다. 받아졌는지만 본다(custom) — google 모듈은 이름마다 Google CSS 를 넣는다(fonts/_loader 참고).
      WebFont.load({
        custom: {
          families: compactedTokens,
        },
        timeout: process.env.NODE_ENV === 'production' ? 15000 : 2000000,
        classes: false,
        fontinactive: function(familyName, fvd) {
          const fontCards = document.querySelectorAll(`[data-font-family-token="${familyName}"]`);
          fontCards.forEach((fontCard) => {
            fontCard.classList.remove("font-loading");
          })
        },
        fontactive: function(familyName, fvd) {
          const fontCards = document.querySelectorAll(`[data-font-family-token="${familyName}"]`);
          fontCards.forEach((fontCard) => {
            fontCard.classList.remove("font-loading");
          })
        },
      });
    }
  }

  visitListView(e) {
    this.updateParams("view", "list");
    const searchParams = new URLSearchParams(this.paramsValue);
    const newUrl = window.location.pathname + "?" + searchParams.toString()
    window.location.href = newUrl;
  }

  visitGridView(e) {
    this.updateParams("view", "");
    const searchParams = new URLSearchParams(this.paramsValue);
    const newUrl = window.location.pathname + "?" + searchParams.toString()
    window.location.href = newUrl;
  }

  updateUrlParams() {
    const searchParams = new URLSearchParams(this.paramsValue);
    const newUrl = window.location.pathname + "?" + searchParams.toString()
    // state 는 기존 것을 그대로 둔다. Turbo 는 popstate 때 state.turbo 가 없으면 무시하므로,
    // 덮어쓰면 다른 페이지로 갔다가 뒤로가기 했을 때 주소만 바뀌고 화면은 그대로 남는다.
    history.replaceState(history.state, '', newUrl);
  }

  reloadFonts() {
    const currentScrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    this.updateUrlParams();
    this.pageValue = 0; // 여기는 클라이언트에서 처음부터 다시그리기 때문에 page 가 0부터 시작한다.

    if (currentScrollTop > 400) {
      window.scrollTo(0, 400)
    }
    this.loadFonts();
  }

  renderNotFound(alternatives = []) {
    this.fontsContainerTarget.innerHTML = this.notFoundTemplateTarget.innerHTML;
    this.renderAlternatives(alternatives);
  }

  // 결과가 0 일 때 "가변 빼기 · 22개" 같은 길을 보여 준다(서버가 alternatives 로 센다).
  renderAlternatives(alternatives) {
    const slot = this.fontsContainerTarget.querySelector("[data-fonts-alternatives]");
    if (!slot || !alternatives || alternatives.length === 0) return;

    const hint = document.createElement("p");
    hint.className = "text-sm text-center text-gray-600 dark:text-gray-300";
    hint.textContent = this.dropFilterHintValue;
    const chips = document.createElement("div");
    chips.className = "flex flex-wrap justify-center gap-2";
    alternatives.forEach(({key, value, label, count}) => {
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "noon-white-button-sm";
      chip.dataset.action = "click->fonts#dropFilter";
      chip.dataset.key = key;
      chip.dataset.value = value;
      chip.textContent = this.dropFilterFormatValue.replace("%{label}", label).replace("%{count}", count.toLocaleString());
      chips.appendChild(chip);
    });
    slot.replaceChildren(hint, chips);
  }

  dropFilter(e) {
    const {key, value} = e.currentTarget.dataset;
    const boxes = {
      variant_traits: this.variantTraitCheckBoxesTargets,
      category_use_ids: this.categoryUseCheckBoxesTargets,
      category_style_ids: this.categoryStyleCheckBoxesTargets,
      file_types: this.fileTypeCheckBoxesTargets,
    }[key] || [];
    const box = boxes.find((checkBox) => checkBox.value === value);
    if (!box) return;
    box.checked = false;
    this.onFilterChange(e);
  }

  loadFonts() {
    this.loadingValue = true;
    const currentPage = this.hasPageValue ? this.pageValue : 1;
    const nextPage = currentPage + 1;

    api.get(`${this.filterApiValue}.json`, {
      page: nextPage, ...this.paramsValue
    }).then((res) => {
      const {fonts, is_last_page, total_count, alternatives} = res.data;
      if (nextPage === 1 && total_count === 0) {
        this.renderNotFound(alternatives);
        return;
      }

      this.renderFonts(fonts);
      this.pageValue = nextPage;
      this.loadingValue = false;
      this.finishedValue = is_last_page;
    })
  }

  renderFonts(fonts) {
    let cardListHTML = "";
    let tokens = []
    fonts.forEach((font) => {
      cardListHTML += this.fontCard(font);
      tokens.push(font.default_variant.font_family_token)
    });

    if (this.fontPartialValue === "grid" && tokens.length > 0) {
      this.fireFontLoader(tokens)
    }
    if (this.pageValue === 0) {
      if (this.fontPartialValue === "grid" && this.displayAdsenseValue && fonts.length > 0) {
        cardListHTML = this.squareAdTemplate() + cardListHTML;
      }
      this.fontsContainerTarget.innerHTML = cardListHTML;
    } else {
      this.fontsContainerTarget.insertAdjacentHTML('beforeend', cardListHTML)
    }
  }

  fontCard(font) {
    let fontTemplate;
    if (this.fontPartialValue === "grid") {
      fontTemplate = this.gridViewTemplate(font);
    } else {
      fontTemplate = this.listViewTemplate(font);
    }
    return fontTemplate;
  }

  onScroll() {
    if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 1500) {
      if (!this.loadingValue && !this.finishedValue) {
        this.loadFonts();
      }
    }
  }

  preventEnterSubmit = (e) => {
    const code = e.keyCode || e.which;
    if (code === 13) {
      e.preventDefault();
      return false;
    }
  }

  toggleTextOutline() {
    this.textOutlineValue = this.hasOutlineCheckboxTarget ? this.outlineCheckboxTarget.checked : !this.textOutlineValue;
    this.applyTextOutline();

    if (this.textOutlineValue) {
      this.updateParams("outline", "1");
    } else {
      this.updateParams("outline", "");
    }
    this.updateUrlParams();
  }

  applyTextOutline() {
    if (this.textOutlineValue) {
      this.fontsContainerTarget.classList.add("outline-enabled");
    } else {
      this.fontsContainerTarget.classList.remove("outline-enabled");
    }
  }

  updateOutlineCheckbox() {
    if (this.hasOutlineCheckboxTarget) {
      this.outlineCheckboxTarget.checked = this.textOutlineValue;
    }
  }

  connect() {
    if (!this.loadingValue && !this.finishedValue) {
      this.renderCheckBoxLabels();
      this.loadFonts();
    }
    this.renderAlternatives(this.alternativesValue); // 서버가 0건으로 그린 첫 화면

    if (this.hasFontEditorAllTarget) {
      this.fontEditorAllTarget.addEventListener("keypress", this.preventEnterSubmit)
    }

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("outline") === "1") {
      this.textOutlineValue = true;
      this.updateOutlineCheckbox();
      this.applyTextOutline();
    }
  }

  disconnect() {
    if (this.hasFontEditorAllTarget) {
      this.fontEditorAllTarget.removeEventListener("keypress", this.preventEnterSubmit)
    }
  }
}
